import React, { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  deleteRisque,
  getRisqueByPlanId,
  addRisque,
} from '../../redux/risques/risqueActions'

import { useAppDispatch, RootState, AppDispatch } from '../../redux/store'
import BarChartOutlined from '@mui/icons-material/BarChartOutlined'
import DriveFileRenameOutlineOutlined from '@mui/icons-material/DriveFileRenameOutlineOutlined'
import CloudUploadOutlined from '@mui/icons-material/CloudUploadOutlined'
import { NavLink, useNavigate, useParams } from 'react-router-dom'
import { getPlanbyId } from '../../redux/plans/planActions'
import UseAlert from '../../components/common/alert'
import { useTranslation } from 'react-i18next'
import { UserRole } from '../../types/keycloak'
import { hasRole } from '../../utils/checkRole'
import { formatTitle } from '../../utils/formatTitle'
import { writeEntityNav } from '../../utils/entityNav'
import { extractRisksFromFileApi } from '../../api/risqueApi'
import { Risque } from '../../types/risqueTypes'
import {
  extractDetectedRisksArray,
  hasVisibleRiskName,
  pickRiskNameFromRecord,
  sanitizeRiskText,
} from '../../utils/planImportRisksPayload'
import ImportRisksWizard, { WizardRisk } from './ImportRisksWizard'
import './ImportRisksWizard.css'
import {
  DetailAccordion,
  DetailButton,
  DetailEntityPanel,
  DetailInfoGrid,
  DetailInfoItem,
  DetailPageHead,
  DetailTable,
} from '../../components/common/detail'
import {
  DetailRowActions,
} from '../../components/common/detail/DetailRowActions'
import {
  ListPageDateCell,
  ListPagePill,
  PRIORITY_PILL,
  RISK_HORIZON_PILL,
  STATUS_PILL,
} from '../../components/common/ListPageCells'
import { ListPageDescription } from '../../components/common/ListPageDescription'
import '../../components/common/ListPageTable.css'
import i18n from '../../i18n'

const PlanDetail = () => {
  const { id } = useParams<{ id: string }>()
  const { t } = useTranslation()
  const navigate = useNavigate()

  const [alert, setAlert] = useState({
    open: false,
    message: '',
    severity: '' as 'info' | 'success' | 'error' | 'warning',
  })

  const handleCloseAlert = () => {
    setAlert({
      ...alert,
      open: false,
    })
  }

  const [detectedRisks, setDetectedRisks] = useState<WizardRisk[]>([])
  const [isImportWizardOpen, setIsImportWizardOpen] = useState(false)
  const [isImportingConfirmedRisks, setIsImportingConfirmedRisks] = useState(false)
  const [isDetectingRisks, setIsDetectingRisks] = useState(false)

  const getSafeErrorMessage = (error: unknown, fallback: string) => {
    if (typeof error === 'string' && error.trim()) return error
    if (error && typeof error === 'object' && 'message' in error) {
      const maybeMessage = (error as { message?: unknown }).message
      if (typeof maybeMessage === 'string' && maybeMessage.trim()) {
        return maybeMessage
      }
    }
    return fallback
  }

  const dispatch: AppDispatch = useAppDispatch()
  const risquesList = useSelector(
    (state: RootState) => state.risque.RisquesbyPlan
  )

  const plan = useSelector((state: RootState) => state.plan.plan)

  const loading = useSelector((state: RootState) => state.risque.loading)
  useEffect(() => {
    if (id) {
      dispatch(getRisqueByPlanId(id))
    }
  }, [dispatch, id])

  useEffect(() => {
    if (id) {
      dispatch(getPlanbyId(id))
    }
  }, [dispatch, id])

  useEffect(() => {
    if (!plan?._id) return
    writeEntityNav({
      planId: plan._id,
      planLabel: formatTitle(plan.PlanName || ''),
      missionId: plan.MissionId || undefined,
      missionLabel: plan.MissionName
        ? formatTitle(plan.MissionName)
        : undefined,
    })
  }, [plan])

  const handleDeleteClick = async (RisqueId: string) => {
    try {
      dispatch(deleteRisque(RisqueId))
      setAlert({
        open: true,
        message: 'delete successfully!',
        severity: 'success',
      })
    } catch (error: any) {
      console.error('Error deleting action:', error.message)
      setAlert({
        open: true,
        message: 'Error deleting action. Please try again later.',
        severity: 'error',
      })
    }
  }

  const fileInputRef = React.useRef<HTMLInputElement>(null)

  const normalizeCriticality = (value: unknown): number => {
    if (typeof value === 'number' && value >= 1 && value <= 4) return value
    if (typeof value === 'string' && value.trim() !== '') {
      const n = parseInt(value.trim(), 10)
      if (!Number.isNaN(n) && n >= 1 && n <= 4) return n
    }
    const normalized = String(value || '')
      .trim()
      .toLowerCase()
    if (normalized === 'critical' || normalized === 'critique') return 1
    if (normalized === 'high' || normalized === 'elevated' || normalized === 'eleve') return 2
    if (normalized === 'medium' || normalized === 'moyen') return 3
    return 4
  }

  const normalizePriority = (value: unknown): number => {
    if (typeof value === 'number' && value >= 1 && value <= 3) return value
    if (typeof value === 'string' && value.trim() !== '') {
      const n = parseInt(value.trim(), 10)
      if (!Number.isNaN(n) && n >= 1 && n <= 3) return n
    }
    const normalized = String(value || '')
      .trim()
      .toLowerCase()
    if (normalized.includes('short') || normalized.includes('court')) return 1
    if (normalized.includes('medium') || normalized.includes('moyen')) return 2
    return 3
  }

  const normalizeDetectedRisk = (risk: any, index: number): WizardRisk => {
    if (typeof risk === 'string') {
      const name = sanitizeRiskText(risk)
      return {
        id: `ai-${index}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        name,
        description: '',
        criticality: 4,
        priority: 2,
        source: 'ai',
      }
    }
    if (!risk || typeof risk !== 'object') {
      return {
        id: `ai-${index}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        name: '',
        description: '',
        criticality: 4,
        priority: 2,
        source: 'ai',
      }
    }
    const rec = risk as Record<string, unknown>
    const name = pickRiskNameFromRecord(rec)
    return {
      id: `ai-${index}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      name,
      description: sanitizeRiskText(
        rec.Description ?? rec.description ?? rec.details ?? rec.Details
      ),
      criticality: normalizeCriticality(rec.criticality ?? rec.Criticality),
      priority: normalizePriority(rec.priority ?? rec.Priority),
      source: 'ai',
    }
  }

  const handleImportRisksClick = () => {
    fileInputRef.current?.click()
  }

  const handleWizardClose = () => {
    setIsImportWizardOpen(false)
    setDetectedRisks([])
  }

  const handleConfirmWizardImport = async (risksToImport: WizardRisk[]) => {
    if (!id || risksToImport.length === 0) return
    setIsImportingConfirmedRisks(true)
    try {
      const payloads: Risque[] = risksToImport.map((risk) => ({
        RisqueName: risk.name,
        Description: risk.description || '',
        Criticality: risk.criticality,
        Priority: risk.priority,
        Deadline: risk.deadline || undefined,
        PlanId: id,
        PlanName: String(plan?.PlanName || ''),
        MissionId: (plan as any)?.MissionId || undefined,
        MissionName: String(plan?.MissionName || ''),
      }))

      await Promise.all(payloads.map((payload) => dispatch(addRisque(payload)).unwrap()))
      await dispatch(getRisqueByPlanId(id)).unwrap()

      setAlert({
        open: true,
        message: t('planImportRisksWizard.successImported', {
          count: risksToImport.length,
        }),
        severity: 'success',
      })
      setIsImportWizardOpen(false)
      setDetectedRisks([])
    } catch (error: any) {
      setAlert({
        open: true,
        message: getSafeErrorMessage(
          error,
          t('planImportRisksWizard.errors.importFailed')
        ),
        severity: 'error',
      })
    } finally {
      setIsImportingConfirmedRisks(false)
    }
  }

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file && id) {
      setIsDetectingRisks(true)
      try {
        const response = await extractRisksFromFileApi(file, id)
        const rawRisks = extractDetectedRisksArray(response)
        const normalized = rawRisks
          .map((risk: any, index: number) => normalizeDetectedRisk(risk, index))
          .filter((risk: WizardRisk) => hasVisibleRiskName(risk.name))

        if (normalized.length === 0) {
          setAlert({
            open: true,
            message: t('planImportRisksWizard.errors.noRisksDetected'),
            severity: 'info',
          })
        } else {
          setDetectedRisks(normalized)
          setIsImportWizardOpen(true)
        }
      } catch (error: any) {
        setAlert({
          open: true,
          message: getSafeErrorMessage(
            error,
            t('planImportRisksWizard.errors.detectFailed')
          ),
          severity: 'error',
        })
      } finally {
        setIsDetectingRisks(false)
        // Reset the input so the same file can be selected again if needed
        if (fileInputRef.current) {
          fileInputRef.current.value = ''
        }
      }
    }
  }
  const roles = useSelector((state: any) => state.auth.roles)
  const [openSections, setOpenSections] = useState({ info: true, risks: true })
  const [search, setSearch] = useState('')

  const riskRows = useMemo(() => {
    const list = Array.isArray(risquesList) ? risquesList : []
    const q = search.trim().toLowerCase()
    if (!q) return list
    return list.filter((r: any) =>
      (r?.RisqueName || '').toLowerCase().includes(q)
    )
  }, [risquesList, search])

  const canUpdate =
    hasRole(roles, UserRole.RSSI) ||
    hasRole(roles, UserRole.SSI_TEAM) ||
    hasRole(roles, UserRole.ADMIN)
  const canAnalyze =
    hasRole(roles, UserRole.RSSI) ||
    hasRole(roles, UserRole.DSSI) ||
    hasRole(roles, UserRole.ADMIN)

  const title =
    formatTitle(plan?.PlanName || '') ||
    t('planDetailsPage.breadcrumbs.planName')

  return (
    <>
      <UseAlert
        open={alert.open}
        message={alert.message}
        severity={alert.severity}
        onClose={handleCloseAlert}
      />
      {isDetectingRisks ? (
        <div
          className="import-risks-detect-overlay"
          role="status"
          aria-live="polite"
          aria-busy="true"
        >
          <div className="import-risks-detect-card">
            <div className="import-risks-detect-spinner" aria-hidden />
            <p className="import-risks-detect-title">
              {t('planImportRisksWizard.detectingShort')}
            </p>
            <p className="import-risks-detect-hint">
              {t('planImportRisksWizard.detecting')}
            </p>
          </div>
        </div>
      ) : null}

      <ImportRisksWizard
        open={isImportWizardOpen}
        risks={detectedRisks}
        loading={isImportingConfirmedRisks}
        onClose={handleWizardClose}
        onConfirmImport={handleConfirmWizardImport}
      />

      <div className="detail-page">
        <DetailPageHead
          eyebrow={t('planDetailsPage.eyebrow', {
            defaultValue: 'Management · Risk treatment plans',
          })}
          title={title}
          subtitle={t('planDetailsPage.subtitle', {
            defaultValue:
              'Plan information and the risks treated under this plan.',
          })}
          actions={
            <>
              {canAnalyze && plan?._id ? (
                <DetailButton
                  variant="ghost"
                  onClick={() =>
                    navigate(`/riskTreatmentPlan/view-analysis/${plan._id}`)
                  }
                >
                  <BarChartOutlined sx={{ fontSize: 16 }} />
                  {t('buttons.viewAnalysis')}
                </DetailButton>
              ) : null}
              {canUpdate ? (
                <DetailButton onClick={() => navigate(`/plan/${plan?._id}`)}>
                  <DriveFileRenameOutlineOutlined sx={{ fontSize: 16 }} />
                  {t('buttons.updatePlan')}
                </DetailButton>
              ) : null}
            </>
          }
        />

        <DetailAccordion
          title={t('planDetailsPage.accordion.information')}
          open={openSections.info}
          onToggle={() =>
            setOpenSections((p) => ({ ...p, info: !p.info }))
          }
        >
          <DetailInfoGrid>
            <DetailInfoItem
              label={t('planDetailsPage.accordion.name')}
              value={title}
            />
            <DetailInfoItem
              label={t('planDetailsPage.accordion.description')}
              value={<ListPageDescription value={plan?.Description} />}
            />
            <DetailInfoItem
              label={t('planDetailsPage.accordion.startDate')}
              mono
              value={
                <ListPageDateCell
                  value={plan?.DateDebut}
                  locale={i18n.language}
                />
              }
            />
            <DetailInfoItem
              label={t('planDetailsPage.accordion.endDate')}
              mono
              value={
                <ListPageDateCell
                  value={plan?.DateFin}
                  locale={i18n.language}
                />
              }
            />
            <DetailInfoItem
              label={t('planDetailsPage.accordion.objectifMaturity')}
              value={plan?.ObjectifMaturite || '—'}
            />
            <DetailInfoItem
              label={t('planDetailsPage.accordion.priority')}
              value={
                plan?.Priority != null ? (
                  <ListPagePill
                    label={
                      t(`priority.${plan.Priority}`, {
                        defaultValue: String(plan.Priority),
                      }) || String(plan.Priority)
                    }
                    bg={
                      (PRIORITY_PILL[Number(plan.Priority)] ?? PRIORITY_PILL[4])
                        .bg
                    }
                    color={
                      (PRIORITY_PILL[Number(plan.Priority)] ?? PRIORITY_PILL[4])
                        .color
                    }
                  />
                ) : (
                  '—'
                )
              }
            />
            <DetailInfoItem
              label={t('planDetailsPage.accordion.mission')}
              value={
                plan?.MissionName ? formatTitle(plan.MissionName) : '—'
              }
            />
            <DetailInfoItem
              label={t('planDetailsPage.accordion.status')}
              value={
                plan?.Status ? (
                  <ListPagePill
                    label={t(plan.Status, { defaultValue: plan.Status })}
                    bg={(STATUS_PILL[plan.Status] ?? { bg: '#EAF0F0' }).bg}
                    color={
                      (STATUS_PILL[plan.Status] ?? { color: '#5F7484' }).color
                    }
                  />
                ) : (
                  '—'
                )
              }
            />
          </DetailInfoGrid>
        </DetailAccordion>

        <DetailAccordion
          title={t('risks', { defaultValue: 'Risks' })}
          count={Array.isArray(risquesList) ? risquesList.length : 0}
          open={openSections.risks}
          onToggle={() =>
            setOpenSections((p) => ({ ...p, risks: !p.risks }))
          }
          headerAction={
            canUpdate ? (
              <>
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                  onChange={handleFileChange}
                  accept=".pdf,.xlsx,.xls,.docx"
                />
                <DetailButton
                  variant="ghost"
                  size="sm"
                  onClick={handleImportRisksClick}
                >
                  <CloudUploadOutlined sx={{ fontSize: 16 }} />
                  {isDetectingRisks
                    ? t('planImportRisksWizard.detectingShort')
                    : t('buttons.importRisks')}
                </DetailButton>
              </>
            ) : null
          }
        >
          <DetailEntityPanel
            listTitle={t('missionDetailsPage.sections.planList', {
              defaultValue: 'Risks',
            })}
            searchPlaceholder={t('planDetailsPage.filterByName')}
            searchValue={search}
            onSearchChange={setSearch}
            canAdd={false}
          >
            <DetailTable<any>
              loading={loading && !riskRows.length}
              loadingLabel={t('loading')}
              emptyTitle={t('missionDetailsPage.empty.title', {
                defaultValue: 'Nothing here yet',
              })}
              emptyMessage={t('noRisks', {
                defaultValue: 'No risks in this plan yet.',
              })}
              rows={riskRows}
              rowKey={(r) => (r?._id || r?.id) as string}
              columns={[
                {
                  key: 'name',
                  header: t('Name'),
                  render: (r) => (
                    <NavLink
                      to={`/risque-detail/${r._id}`}
                      className="incyia-name-link"
                    >
                      <ListPageDescription
                        value={
                          r.RisqueName
                            ? formatTitle(r.RisqueName)
                            : undefined
                        }
                        tone="primary"
                      />
                    </NavLink>
                  ),
                },
                {
                  key: 'desc',
                  header: t('Description'),
                  render: (r) => (
                    <ListPageDescription value={r.Description} />
                  ),
                },
                {
                  key: 'crit',
                  header: t('Criticality'),
                  render: (r) => {
                    const c = Number(r.Criticality)
                    const style = PRIORITY_PILL[c] ?? PRIORITY_PILL[4]
                    return (
                      <ListPagePill
                        label={
                          t(`priority.${c}`, {
                            defaultValue: style.label,
                          }) || style.label
                        }
                        bg={style.bg}
                        color={style.color}
                      />
                    )
                  },
                },
                {
                  key: 'prio',
                  header: t('Priority'),
                  render: (r) => {
                    const p = Number(r.Priority)
                    const style = RISK_HORIZON_PILL[p] ?? RISK_HORIZON_PILL[3]
                    return (
                      <ListPagePill
                        label={t(`prioritRisk.${p}`, {
                          defaultValue: String(p),
                        })}
                        bg={style.bg}
                        color={style.color}
                      />
                    )
                  },
                },
              ]}
              actionsHeader={t('Actions', { defaultValue: 'Action' })}
              renderActions={(r) => (
                <DetailRowActions
                  roles={roles}
                  onEdit={() => navigate(`/risque/${r._id}`)}
                  onDelete={() => handleDeleteClick(r._id as string)}
                  deleteItem={{
                    entityType: t('risks', { defaultValue: 'Risk' }),
                    name: r.RisqueName
                      ? formatTitle(r.RisqueName)
                      : undefined,
                    details: [
                      {
                        label: t('deleteConfirm.fields.mission'),
                        value: r.MissionName
                          ? formatTitle(r.MissionName)
                          : plan?.MissionName
                            ? formatTitle(plan.MissionName)
                            : '—',
                      },
                      {
                        label: t('deleteConfirm.fields.status'),
                        value: r.Status || '—',
                      },
                    ],
                  }}
                />
              )}
            />
          </DetailEntityPanel>
        </DetailAccordion>
      </div>
    </>
  )
}

export default PlanDetail
